'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import {
  BlogTLDR,
  BlogKeyTakeaways,
  BlogFAQ,
  BlogSources,
  BlogLastUpdated,
  BlogComparisonTable,
  BlogAppCTA,
  BlogWhatTheAppChecks,
} from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Canned Tuna Have Microplastics? Brands & Can Liners Compared (2026)"
      author="MicroPlastics Research Desk"
      date="July 31, 2026"
      readTime="10 min read"
      heroImage="/blog/heroes/microplastics-in-canned-food.webp"
      heroImageAlt="Cans and glass jars of tuna on a kitchen counter"
    >
      <BlogLastUpdated iso="2026-07-31" formatted="July 31, 2026" />

      <BlogTLDR>
        <strong>Probably some, but less than canned sardines, and the can lining
        is the bigger issue than the fish.</strong> The study people cite for this
        question examined <strong>canned sardines and sprats</strong> and found
        microplastics in some samples. That result does not transfer cleanly to
        tuna, because of how the two products are made: small fish are canned{' '}
        <strong>whole, with the digestive tract intact</strong>, and the gut is
        exactly where ingested marine microplastics concentrate. Tuna is sold as{' '}
        <strong>gutted, skinned loin</strong> — the organs are removed before
        canning. Muscle tissue carries far fewer particles than gut content. So
        the honest ranking puts the <strong>epoxy or acrylic can liner</strong>{' '}
        and the packing liquid ahead of the fish itself, and puts{' '}
        <strong>tuna in a glass jar</strong> at the top of the list.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>The primary research is on canned sardines and sprats, not tuna. Those fish are eaten whole, including the gut where microplastics accumulate.</>,
          <>Tuna is canned as gutted, skinned loin, so the fish-borne particle load is structurally lower than for whole small fish.</>,
          <>Every food can has an internal epoxy or acrylic liner. For tuna that liner, not the fish, is the main plastics question.</>,
          <>Oil-packed tuna is a more aggressive migration environment than water-packed, because many liner additives are fat-soluble.</>,
          <>Tuna in glass jars (Ortiz, Tonnino and similar) removes the liner question entirely and is the cleanest option.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_canned_tuna" />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the research actually looked at
      </h2>
      <p>
        A 2018 study published in <em>Scientific Reports</em> examined{' '}
        <strong>canned sardines and sprats</strong> from twenty brands across
        thirteen countries and found microplastics in a minority of the samples.
        It is the study almost every &ldquo;microplastics in canned fish&rdquo;
        headline traces back to, and it is good work. It is also{' '}
        <strong>not about tuna</strong>, and the difference is not pedantic.
      </p>
      <p>
        Fish ingest microplastics from seawater and prey, and those particles
        accumulate overwhelmingly in the{' '}
        <strong>gastrointestinal tract</strong>, not in muscle. That distinction
        decides how much ends up on your plate:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Sardines, sprats, anchovies:</strong> canned whole. Head
          sometimes removed, but the viscera typically stay. You eat the gut, so
          you eat what was in it.
        </li>
        <li>
          <strong>Tuna:</strong> a large fish, butchered into{' '}
          <strong>loin</strong>. Skin, bones and organs are removed before
          cooking and canning. The gut never reaches the can.
        </li>
      </ul>
      <p>
        This makes canned tuna one of the{' '}
        <strong>better</strong> seafood choices on microplastics specifically,
        which is the opposite of how it is usually framed. Note this is a
        statement about microplastics alone — tuna carries a well-established{' '}
        <strong>methylmercury</strong> question that small fish largely do not,
        and that is the reason existing advice tells pregnant women to limit
        albacore. Nothing here changes that.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The can liner is the real story
      </h2>
      <p>
        Every food can is lined. Bare steel or aluminium would react with the
        contents, so the interior carries a thin polymer coating, historically an{' '}
        <strong>epoxy resin</strong> made using bisphenol A. Most large brands
        have moved to <strong>BPA-NI</strong> liners, meaning BPA is not
        intentionally added, typically substituting acrylic, polyester, or
        oleoresin coatings. &ldquo;Non-intent&rdquo; is a precise and limited
        claim: it means the manufacturer does not use BPA as an input, not that
        the coating is inert or that its replacements are fully characterised.
      </p>
      <p>
        Three factors make tuna cans a more demanding case than, say, canned
        beans:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Retort sterilisation.</strong> Cans are heated under pressure
          after sealing, often above 115&deg;C. That heat is applied while the
          food is already in contact with the liner, which is the most
          migration-favourable moment in the product&apos;s life.
        </li>
        <li>
          <strong>Oil packing.</strong> Many liner additives are{' '}
          <strong>lipophilic</strong>, so they dissolve into fat far more readily
          than into water. Oil-packed tuna is a more aggressive extraction medium
          than brine or spring water. Food-contact regulations treat fatty foods
          as a harsher test condition for exactly this reason.
        </li>
        <li>
          <strong>Long shelf life.</strong> Migration continues slowly over
          years. A can bought three years into a five-year shelf life has had
          longer contact than a fresh one.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Canned tuna compared
      </h2>
      <p>
        No brand publishes microplastic testing on its tuna, so this ranks the
        variables that the research connects to exposure: packaging material,
        packing medium, and whether the fish is whole or loin.
      </p>

      <BlogComparisonTable
        caption="Canned and jarred tuna through a plastics lens. Liner formulations change, so treat brand rows as current-practice guidance rather than a lab result."
        columns={[
          { key: 'brand', label: 'Product' },
          { key: 'pack', label: 'Packaging' },
          { key: 'medium', label: 'Packing medium' },
          { key: 'verdict', label: 'Verdict' },
        ]}
        rows={[
          { brand: 'Ortiz / Tonnino (glass jar)', pack: 'Glass jar, metal lid w/ small liner', medium: 'Olive oil', verdict: 'Best. No can liner at all' },
          { brand: 'Fresh or frozen tuna steak', pack: 'None, or paper', medium: 'None', verdict: 'Best. No packaging contact' },
          { brand: 'Safe Catch', pack: 'BPA-NI lined can', medium: 'Water, no drain', verdict: 'Strong. Water-packed, tests each fish for mercury' },
          { brand: 'Wild Planet', pack: 'BPA-NI lined can', medium: 'Own juices, no added water', verdict: 'Strong. Water-based medium' },
          { brand: 'StarKist (water)', pack: 'BPA-NI lined can', medium: 'Spring water', verdict: 'Fine. Water-packed is the better choice' },
          { brand: 'Bumble Bee (water)', pack: 'BPA-NI lined can', medium: 'Water', verdict: 'Fine. Water-packed is the better choice' },
          { brand: 'Chicken of the Sea (water)', pack: 'BPA-NI lined can', medium: 'Water', verdict: 'Fine. Water-packed is the better choice' },
          { brand: 'Any brand, oil-packed', pack: 'Lined can', medium: 'Soybean or olive oil', verdict: 'Weaker. Fat pulls more from the liner' },
          { brand: 'Tuna in a flexible pouch', pack: 'Multi-layer plastic laminate', medium: 'Water or oil', verdict: 'Weak. Direct food-to-plastic contact, retorted' },
          { brand: 'Canned sardines / sprats', pack: 'Lined can', medium: 'Oil or brine', verdict: 'Higher on microplastics: whole fish, gut included' },
        ]}
      />
      <p>
        Two conclusions fall out of that table.{' '}
        <strong>Water-packed beats oil-packed</strong> within any brand, and the
        difference between brands is smaller than the difference between formats.
        And <strong>the pouch is worse than the can</strong>, which surprises
        people — a foil-and-plastic laminate puts food against a polymer layer
        directly and then heats the whole assembly, whereas a can at least has a
        rigid metal body with a thin coating.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical guidance
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Choose water-packed over oil-packed</strong> if you are optimising for liner migration. If you want the oil for flavour, buy the glass jar instead.</li>
        <li><strong>Buy glass jars where you can.</strong> Spanish and Italian jarred tuna costs more per gram but removes the liner question entirely and is better fish.</li>
        <li><strong>Skip the pouches.</strong> Convenient, but direct plastic contact through a retort process.</li>
        <li><strong>Don&apos;t store opened tuna in the can.</strong> Once opened, the liner is scratched and exposed. Move leftovers to glass.</li>
        <li><strong>Never heat food in the can.</strong> Applying heat to a lined can is the worst thing you can do with it.</li>
        <li><strong>Keep the mercury guidance separate.</strong> Skipjack and light tuna are lower in mercury than albacore. Microplastics and mercury are different problems and the answers do not line up.</li>
      </ul>
      <p>
        For the underlying liner chemistry across all canned goods, see{' '}
        <Link href="/blog/are-cans-lined-with-plastic" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          are cans lined with plastic
        </Link>{' '}
        and{' '}
        <Link href="/blog/microplastics-in-canned-food" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in canned food
        </Link>
        .
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Whether your tuna is in a lined can, a flexible pouch, or a glass jar, and whether it is packed in oil or water.</>,
          <>A 0&ndash;100 risk score that separates the packaging question from the fish itself.</>,
          <>A lower-plastic swap at a similar price, usually the water-packed or jarred version of what you already buy.</>,
          <>Your running pantry profile, so canned staples are scored as a weekly pattern rather than one tin.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan your pantry tins"
        sub="Water or oil, can or pouch or jar, changes the answer more than the brand does. Scan the tuna in your cupboard with MicroPlastics for a 0&ndash;100 score and a cleaner version of the same thing."
        buttonLabel="Scan my canned food"
      />

      <p>
        Related:{' '}
        <Link href="/blog/microplastics-in-canned-food" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in canned food
        </Link>
        ,{' '}
        <Link href="/blog/are-cans-lined-with-plastic" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          are cans lined with plastic
        </Link>
        ,{' '}
        <Link href="/blog/foods-with-the-most-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          foods with the most microplastics
        </Link>
        , and{' '}
        <Link href="/blog/best-plastic-free-food-storage" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best plastic-free food storage
        </Link>
        .
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Does canned tuna have microplastics?',
            answer:
              'Likely some, but less than canned sardines or sprats. The often-cited 2018 research examined whole small fish, which are canned with the digestive tract intact, and the gut is where ingested marine microplastics concentrate. Tuna is canned as gutted, skinned loin, so the organs never reach the can. For tuna, the epoxy or acrylic can liner is the more relevant question than the fish.',
          },
          {
            question: 'Is tuna in a pouch better than tuna in a can?',
            answer:
              'No, it is generally worse for plastics. A flexible pouch is a multi-layer plastic and foil laminate, so the food sits in direct contact with a polymer layer and then the whole sealed package is heat-sterilised. A can has a rigid metal body with a thin internal coating. If you want to avoid liners altogether, a glass jar is the option that actually does it.',
          },
          {
            question: 'Should I buy tuna packed in water or oil?',
            answer:
              'Water, if you are optimising for what comes out of the can liner. Many liner additives are fat-soluble, so oil is a considerably more effective extraction medium than brine or spring water, and the can is heat-sterilised while that contact is happening. If you want oil-packed tuna for flavour or texture, buying it in a glass jar gets you both.',
          },
          {
            question: 'Are BPA-free tuna cans actually safe?',
            answer:
              'BPA-NI, or "non-intent", means the manufacturer does not intentionally use bisphenol A in the liner, typically substituting acrylic, polyester or oleoresin coatings. It is a genuine improvement on older epoxy liners but a narrow claim: it does not mean the coating is inert, and the replacement chemistries are less thoroughly studied than BPA itself. Glass avoids the question rather than reformulating it.',
          },
          {
            question: 'Which canned fish has the most microplastics?',
            answer:
              'Small fish eaten whole, such as sardines, sprats and anchovies, because the digestive tract is included and that is where microplastics accumulate in fish. Tuna, salmon and other species sold as gutted fillet or loin carry less from the fish itself. Note that this ordering is specific to microplastics and runs opposite to mercury, where small fish are the safer choice.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Karami A, Golieskardi A, Choo CK, et al.',
            year: 2018,
            title: 'Microplastic and mesoplastic contamination in canned sardines and sprats',
            publisher: 'Science of the Total Environment',
            url: 'https://www.sciencedirect.com/science/article/abs/pii/S0048969717332321',
          },
          {
            authors: 'Akhbarizadeh R, Moore F, Keshavarzi B',
            year: 2018,
            title: 'Investigating microplastics bioaccumulation and biomagnification in seafood from the Persian Gulf',
            publisher: 'Environmental Pollution',
            url: 'https://www.sciencedirect.com/science/article/abs/pii/S0269749118307279',
          },
          {
            authors: 'Cox KD, Covernton GA, Davies HL, et al.',
            year: 2019,
            title: 'Human consumption of microplastics',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.9b01517',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
